import React from "react";
import { useTranslation } from "react-i18next";
import { Box, Grid, Typography, useMediaQuery, useTheme } from "@mui/material";
import { useLanguageTheme } from "../../../../hooks/useLanguageTheme";
import { useAuth } from "../../../../hooks/useAuth";
import { howToJoinData } from "../../constants";
import {
  getMarginBottom,
  getMarginTop,
  getMarginLeft,
  getMarginRight,
} from "../../hooks/useResponsibleMargin";
import { useAffiliateNavigation } from "../../hooks/useAffiliateNavigation";
import { useUserProfile } from "../../../../hooks/useUserProfile";

export const HowToJoinSection = ({
  howToJoinRef,
}: {
  howToJoinRef: React.RefObject<HTMLDivElement>;
}) => {
  const { t, i18n } = useTranslation(["affiliates"]);
  const isRTL = i18n.dir() === "rtl";
  const langTheme = useLanguageTheme();

  const isAuthenticated = useAuth();
  const { handleAffiliateNavigation } = useAffiliateNavigation();
  const { profile }: { profile: any } = useUserProfile();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const downMd = useMediaQuery(theme.breakpoints.down("md"));
  const isRotatedMobile = useMediaQuery(theme.breakpoints.down(800));
  const titleFont = isRTL ? "Noto Sans Hebrew" : "Frank Ruhl Libre";
  const bodyFont = isRTL ? "Noto Sans Hebrew" : "DM Sans";

  return (
    <div
      ref={howToJoinRef}
      className="xxl-width"
      style={{
        display: "flex",
        justifyContent: "center",
        backgroundColor: "#034A73",
        padding: downMd ? "0px 20px" : "20px",
        boxSizing: "border-box",
        paddingBottom: downMd ? "45px" : "75px",
        scrollMarginTop: "100px",
      }}
    >
      <div className="xl-width">
        <Box
          sx={{
            color: "white",
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            flexDirection: "column",
          }}
        >
          {/* Title Section */}
          <Box
            sx={{
              alignSelf: "center",
              justifySelf: "center",
              textAlign: isRotatedMobile ? "center" : isRTL ? "right" : "left",
              fontFamily: titleFont,
              marginBottom: downMd ? "25px" : "45px",
              marginTop: downMd ? "45px" : "55px",
            }}
          >
            <Typography
              sx={{
                mb: 1,
                fontWeight: 600,
                fontSize: downMd ? "24px" : "36px",
                textTransform: "uppercase",
                lineHeight: isMobile ? "30px" : "46px",
                fontFamily: isRTL ? "Noto Sans Hebrew" : "Frank Ruhl Libre",
              }}
            >
              {t("how_to_to_join_title")}
            </Typography>
          </Box>
          {/* Benefits Section */}
          <Box
            sx={{
              maxWidth: "960px",
              width: "100%",
              position: "relative",
              order: isMobile ? 2 : isRTL ? 1 : 2,
              overflow: "hidden",
            }}
          >
            <Grid
              container
              spacing={0}
              sx={{
                position: "relative",
                direction: i18n.dir(),
              }}
            >
              {howToJoinData.map((benefit, index) => (
                <Grid
                  item
                  xs={12}
                  sm={6}
                  key={index}
                  sx={{
                    mb: isMobile ? "20px" : 0,
                    borderBottom:
                      isMobile && index !== 3
                        ? "1px solid rgba(255, 255, 255, 0.34)"
                        : "none",
                  }}
                >
                  <Box
                    sx={{
                      position: "relative",
                      pb: 0,
                      display: "flex",
                      alignItems: "center",
                    }}
                  >
                    {/* Single Container for Icon + Text */}

                    <Box
                      sx={{
                        display: "flex",
                        direction: i18n.dir(),
                        flexDirection: "column",
                        mb: getMarginBottom(isMobile, index, isRTL),
                        mt: getMarginTop(isMobile, index),
                        marginLeft: getMarginLeft(isMobile, isRTL, index),
                        marginRight: getMarginRight(isMobile, isRTL, index),
                        justifyContent: "space-between",
                        width: "100%",
                        marginBottom: isMobile ? "20px" : "30px",
                      }}
                    >
                      <Box
                        sx={{
                          minHeight: "44px",
                          minWidth: "44px",
                          borderRadius: "50%",
                          background:
                            "linear-gradient(30deg, #4698C6, #FCBC5CB8 72%)",
                          display: "flex",
                          justifyContent: "center",
                          alignItems: "center",
                          width: "fit-content",
                          mx: 0,
                          fontFamily: langTheme.mainFont(),
                          fontSize: "24px",
                          lineHeight: "24px",
                        }}
                      >
                        <div>{index + 1}</div>
                      </Box>
                      <Box
                        sx={{
                          mx: { xs: 0, sm: "15px", md: "15px" },
                          textAlign: isRTL ? "right" : "left",
                          fontFamily: bodyFont,
                          height: "100%",
                        }}
                      >
                        <Typography
                          variant="h6"
                          sx={{
                            mt: 2,
                            mb: 1,
                            fontWeight: 600,
                            fontSize: "20px",
                            fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
                            letterSpacing: "4%",
                            lineHeight: "20px",
                            textDecoration: index === 0 ? "underline" : "none",
                            cursor: index === 0 ? "pointer" : "auto",
                          }}
                          onClick={
                            index === 0
                              ? () =>
                                  handleAffiliateNavigation(
                                    isAuthenticated,
                                    profile
                                  )
                              : undefined
                          }
                        >
                          {t(benefit.title)}
                        </Typography>
                        <Typography
                          variant="body2"
                          sx={{
                            fontSize: { xs: "16px", sm: "16px" },
                            fontFamily: isRTL ? "Noto Sans Hebrew" : "DM sans",
                            lineHeight: "20px",
                            fontWeight: 400,
                            color: "#FFFFFFE0",
                            marginTop: "15px",
                          }}
                        >
                          {t(benefit.description)}
                        </Typography>
                      </Box>
                    </Box>
                  </Box>
                </Grid>
              ))}
              {/* Vertical Border for Desktop */}
              {!isMobile && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "-7%",
                      left: !isRTL ? "50%" : "auto",
                      right: isRTL ? "50%" : "auto",
                      width: "1px",
                      height: "55%",
                      backgroundColor: "#FFFFFF57",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "53%",
                      left: !isRTL ? "50%" : "auto",
                      right: isRTL ? "50%" : "auto",
                      width: "1px",
                      height: "53%",
                      backgroundColor: "#FFFFFF57",
                    }}
                  />
                </>
              )}
              {/* Horizontal Border for Desktop */}
              {!isMobile && (
                <>
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: 0,
                      right: "53%",
                      width: "49%",
                      height: "1px",
                      backgroundColor: "#FFFFFF57",
                    }}
                  />
                  <Box
                    sx={{
                      position: "absolute",
                      top: "50%",
                      left: "51%",
                      right: 0,
                      width: "48%",
                      height: "1px",
                      backgroundColor: "#FFFFFF57",
                    }}
                  />
                </>
              )}
            </Grid>
          </Box>
        </Box>
      </div>
    </div>
  );
};
